import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowRight, ArrowUpRight, AlertCircle, Mail } from 'lucide-react';
import { Logo } from './Logo';

const FOOTER_LINKS = [
  { label: 'Privacy', href: '/privacy' },
  { label: 'Terms', href: '/terms' },
  { label: 'Cookies', href: '/cookies' },
  { label: 'Help & Legal', href: '/help-legal' },
  { label: 'Trust Center', href: '/trust' },
];

/**
 * Shared layout for legal pages — premium navy/red/white branding, mobile responsive,
 * top notice banner, bottom legal nav + CTA buttons.
 *
 * `sections` is an array of { type: 'h1' | 'h2' | 'p' | 'ul', text?: string, items?: string[] }
 */
export default function LegalLayout({ title, subtitle, effectiveDate, sections = [] }) {
  const { pathname } = useLocation();

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid={`legal-page-${pathname.replace('/', '')}`}>
      {/* Header */}
      <header className="bg-[#0f1b3d] text-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo size="sm" variant="light" linkTo="/" />
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-[12.5px] text-white/70 hover:text-white transition-colors"
            data-testid="legal-back-home"
          >
            Back to home <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-6 py-12 sm:py-16">
          {/* Title block */}
          <div className="mb-10">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#DA291C] mb-3">
              ShiftCover365
            </p>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 leading-tight">
              {title}
            </h1>
            {subtitle && (
              <p className="mt-3 text-[15px] sm:text-base text-slate-600 leading-relaxed">{subtitle}</p>
            )}
            {effectiveDate && (
              <p className="mt-3 text-[12.5px] text-slate-400">Effective date: {effectiveDate}</p>
            )}
          </div>

          {/* Sections */}
          <article className="space-y-6">
            {sections.map((s, i) => {
              if (s.type === 'h1') {
                return (
                  <h2 key={i} className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight mt-10 first:mt-0 pb-2 border-b border-slate-200">
                    {s.text}
                  </h2>
                );
              }
              if (s.type === 'h2') {
                return (
                  <h3 key={i} className="text-[15px] font-semibold text-slate-900 mt-5">
                    {s.text}
                  </h3>
                );
              }
              if (s.type === 'p') {
                return (
                  <p key={i} className="text-[14.5px] sm:text-[15px] text-slate-700 leading-[1.75]">
                    {s.text}
                  </p>
                );
              }
              if (s.type === 'ul') {
                return (
                  <ul key={i} className="space-y-2 pl-1">
                    {s.items.map((it, j) => (
                      <li key={j} className="text-[14.5px] text-slate-700 leading-relaxed flex gap-2.5">
                        <span className="text-[#DA291C] font-bold shrink-0">·</span>
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                );
              }
              return null;
            })}
          </article>

          {/* Bottom legal nav */}
          <div className="mt-16 pt-6 border-t border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <nav className="flex flex-wrap items-center gap-x-1.5 gap-y-2 text-[12.5px]" data-testid="legal-bottom-nav">
              {FOOTER_LINKS.map((l, i) => (
                <span key={l.href} className="flex items-center gap-1.5">
                  {i > 0 && <span className="text-slate-300">|</span>}
                  <Link
                    to={l.href}
                    className={`transition-colors ${pathname === l.href ? 'text-slate-900 font-semibold' : 'text-slate-500 hover:text-slate-900'}`}
                  >
                    {l.label}
                  </Link>
                </span>
              ))}
            </nav>
            <p className="text-[11.5px] text-slate-400">© {new Date().getFullYear()} ShiftCover365 · All rights reserved.</p>
          </div>
        </div>
      </main>
    </div>
  );
}
